import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CopyButton } from '@/common/components/CopyButton'
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ResumeBox from '@/common/components/ResumeBox'
import { ShowHeader } from '@/common/components/ShowHeader'
import { useCopyButton } from '@/common/hooks/useCopyButton'
import { formatCpf, formatCurrency, formatDateTime, formatDecimalUnit } from '@/common/utils/formatters'
import { ReprocessTmaisNfceModal } from '@/features/tmaisNfces/components/ReprocessTmaisNfceModal'
import { tmaisNfcesStatusStrategy } from '@/features/tmaisNfces/constants/statusOptions.constant'
import { useTmaisNfce } from '@/features/tmaisNfces/hooks/useTmaisNfce'
import { useParams } from 'react-router-dom'

const Show = () => {
  const { id } = useParams()

  const { control, copySuccess } = useCopyButton()

  const {
    data: nfce,
    isPending: nfceIsPending,
    error: nfceError = {} as any,
    refetch: refetchNfce,
  } = useTmaisNfce(id || '')

  const statusLabel = nfce?.status
    ? tmaisNfcesStatusStrategy[nfce.status as keyof typeof tmaisNfcesStatusStrategy]
    : '--'

  const enableReprocess = ['failed', 'rejected'].includes(nfce?.status || '')

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <ShowHeader
        goBack
        goBackProps={{ fallback: '/tmais-nfces' }}
        title="NFCe Escaneadas"
        subtitle={nfce?.id}
        isLoading={nfceIsPending}
        buttons={enableReprocess ? <ReprocessTmaisNfceModal tmaisNfceId={id} /> : undefined}
      />

      {nfceIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={nfceError?.status}
          refetchFunction={refetchNfce}
        >
          <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            <ResumeBox
              label="URL"
              value={
                <div className="flex items-center gap-4">
                  <p className="text-sm break-all">{nfce?.url}</p>

                  <Button
                    variant="link-white"
                    disabled={!nfce?.url}
                    onClick={() => nfce?.url && copySuccess(nfce?.url)}
                  >
                    <CopyButton control={control} className="size-4" />
                  </Button>
                </div>
              }
            />

            <div className="flex gap-2">
              <ResumeBox
                label="Estabelecimento"
                value={nfce?.establishment_company_branch?.general_information?.fantasy_name ?? 'Taurus (Global)'}
                boxClassName="w-full"
              />

              <ResumeBox
                label="CPF do comprador"
                value={nfce?.buyer_cpf ? formatCpf(nfce.buyer_cpf) : '--'}
                boxClassName="w-full"
              />
            </div>

            <div className="flex gap-2">
              <ResumeBox label="Processada por" value={nfce?.uploaded_by?.full_name ?? '--'} boxClassName="w-full" />

              <ResumeBox
                label="CPF do usuário que processou a nota"
                value={nfce?.uploaded_by?.cpf ? formatCpf(nfce.uploaded_by.cpf) : '--'}
                boxClassName="w-full"
              />
            </div>

            <div className="flex gap-2">
              <ResumeBox label="Status" value={statusLabel} boxClassName="w-full" />

              {nfce?.failure_reason && (
                <ResumeBox
                  label="Motivo"
                  value={nfce.failure_reason}
                  boxClassName="w-full"
                  valueClassName="break-all"
                />
              )}
            </div>

            <div className="flex gap-2">
              <ResumeBox
                label="Valor Total"
                value={nfce?.total_amount ? formatCurrency(Number(nfce.total_amount)) : '--'}
                boxClassName="w-full"
              />

              <ResumeBox
                label="Processada em"
                value={nfce?.created_at ? formatDateTime(new Date(nfce.created_at)) : '--'}
                boxClassName="w-full"
              />
            </div>

            <Accordion
              name="produtos"
              defaultOpen
              head={<p className="text-base font-bold">Produtos</p>}
              body={
                <div className="flex flex-col gap-4">
                  {!nfce?.line_items?.length ? (
                    <EmptyDataMessage className="max-h-72" message="Esta NFCe não possui produtos" />
                  ) : (
                    nfce?.line_items?.map((item) => (
                      <Box key={item.id} className="flex flex-col gap-2 p-4">
                        <div className="flex gap-2">
                          <ResumeBox label="Nome" value={item.original_name} boxClassName="w-full" />

                          <ResumeBox label="Produto" value={item.product?.display_name || '--'} boxClassName="w-full" />
                        </div>

                        <div className="flex gap-2">
                          <ResumeBox
                            label="Quantidade"
                            value={formatDecimalUnit(item.quantity, item.product?.unit_for_quantity)}
                            boxClassName="w-full"
                          />

                          <ResumeBox
                            label="Valor total"
                            value={formatCurrency(Number(item.total_amount))}
                            boxClassName="w-full"
                          />
                        </div>
                      </Box>
                    ))
                  )}
                </div>
              }
            />
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  )
}

export default Show
